import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Table from '../components/Table';
import {
  fetchMasterCategories,
  fetchMasterServices,
  addMasterCategory,
  updateMasterCategory,
  setMasterCategoryEnabled,
  addMasterService,
  updateMasterService,
  setMasterServiceEnabled,
  seedMasterData
} from '../utils/masterCatalog';
import { DURATIONS, DEFAULT_DURATION, GENDERS, DEFAULT_GENDER } from '../constants/masterData';
import './MasterCatalog.css';

const emptyCategoryForm = { name: '', sortOrder: '' };
const emptyServiceForm = {
  name: '',
  categoryId: '',
  gender: DEFAULT_GENDER,
  defaultDuration: DEFAULT_DURATION,
  subServices: []
};

function MasterCatalog() {
  const [activeTab, setActiveTab] = useState('services');
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  // Category modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);

  // Service modal
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState(emptyServiceForm);

  // Service filters
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterGender, setFilterGender] = useState('all');
  const [searchText, setSearchText] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [cats, svcs] = await Promise.all([fetchMasterCategories(), fetchMasterServices()]);
      setCategories(cats);
      setServices(svcs);
    } catch (err) {
      setError('Failed to load master catalog. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSeed = async () => {
    const confirmed = window.confirm(
      'Seed the master catalog with default categories and services?\n\nExisting entries are never modified or duplicated — only missing defaults are added.'
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      const { categoriesAdded, servicesAdded, servicesUpdated } = await seedMasterData();
      await loadData();
      alert(
        `Seeding complete: ${categoriesAdded} categories and ${servicesAdded} services added` +
          (servicesUpdated ? `, ${servicesUpdated} existing services gained new sub services.` : '.')
      );
    } catch (err) {
      alert(`Failed to seed master data: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // ---------- Categories ----------

  const openAddCategory = () => {
    setEditingCategory(null);
    const maxOrder = categories.reduce((max, c) => Math.max(max, c.sortOrder || 0), 0);
    setCategoryForm({ name: '', sortOrder: String(maxOrder + 10) });
    setShowCategoryModal(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name || '', sortOrder: String(category.sortOrder ?? 0) });
    setShowCategoryModal(true);
  };

  const handleCategorySubmit = async () => {
    const name = categoryForm.name.trim();
    if (!name) {
      alert('Category name is required');
      return;
    }
    const duplicate = categories.find(
      (c) => c.nameLower === name.toLowerCase() && c.id !== editingCategory?.id
    );
    if (duplicate) {
      alert(`A category named "${duplicate.name}" already exists`);
      return;
    }

    try {
      setActionLoading(true);
      if (editingCategory) {
        await updateMasterCategory(editingCategory.id, { name, sortOrder: categoryForm.sortOrder });
      } else {
        await addMasterCategory({ name, sortOrder: categoryForm.sortOrder });
      }
      setShowCategoryModal(false);
      await loadData();
    } catch (err) {
      alert(`Failed to save category: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleCategory = async (category) => {
    const action = category.enabled ? 'Disable' : 'Enable';
    if (!window.confirm(`${action} category "${category.name}"?`)) return;
    try {
      setActionLoading(true);
      await setMasterCategoryEnabled(category.id, !category.enabled);
      await loadData();
    } catch (err) {
      alert(`Failed to update category: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // ---------- Services ----------

  const openAddService = () => {
    setEditingService(null);
    setServiceForm({ ...emptyServiceForm, categoryId: categories.find((c) => c.enabled)?.id || '' });
    setShowServiceModal(true);
  };

  const openEditService = (service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name || '',
      categoryId: service.categoryId || '',
      gender: service.gender || DEFAULT_GENDER,
      defaultDuration: service.defaultDuration || DEFAULT_DURATION,
      subServices: (service.subServices || []).map((s) => ({
        name: s.name || '',
        defaultDuration: s.defaultDuration || DEFAULT_DURATION
      }))
    });
    setShowServiceModal(true);
  };

  const handleServiceSubmit = async () => {
    const name = serviceForm.name.trim();
    if (!name) {
      alert('Service name is required');
      return;
    }
    if (!serviceForm.categoryId) {
      alert('Please select a category');
      return;
    }
    const duplicate = services.find(
      (s) => s.nameLower === name.toLowerCase() && s.id !== editingService?.id
    );
    if (duplicate) {
      alert(`A service named "${duplicate.name}" already exists`);
      return;
    }

    const category = categories.find((c) => c.id === serviceForm.categoryId);
    const payload = {
      name,
      categoryId: serviceForm.categoryId,
      categoryName: category?.name || '',
      gender: serviceForm.gender,
      defaultDuration: serviceForm.defaultDuration,
      subServices: serviceForm.subServices
    };

    try {
      setActionLoading(true);
      if (editingService) {
        await updateMasterService(editingService.id, payload);
      } else {
        await addMasterService(payload);
      }
      setShowServiceModal(false);
      await loadData();
    } catch (err) {
      alert(`Failed to save service: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleService = async (service) => {
    const action = service.enabled ? 'Disable' : 'Enable';
    if (!window.confirm(`${action} service "${service.name}"?`)) return;
    try {
      setActionLoading(true);
      await setMasterServiceEnabled(service.id, !service.enabled);
      await loadData();
    } catch (err) {
      alert(`Failed to update service: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const updateSubService = (index, field, value) => {
    setServiceForm((form) => ({
      ...form,
      subServices: form.subServices.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    }));
  };

  const filteredServices = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return services.filter((s) => {
      if (filterCategory !== 'all' && s.categoryId !== filterCategory) return false;
      if (filterGender !== 'all' && s.gender !== filterGender) return false;
      if (q && !(s.nameLower || '').includes(q)) return false;
      return true;
    });
  }, [services, filterCategory, filterGender, searchText]);

  const categoryColumns = [
    { key: 'name', title: 'Category' },
    { key: 'sortOrder', title: 'Sort Order' },
    {
      key: 'enabled',
      title: 'Status',
      render: (value) => (
        <span className={`status-badge ${value ? 'status-approved' : 'status-rejected'}`}>
          {value ? 'Enabled' : 'Disabled'}
        </span>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="mc-row-actions">
          <button className="mc-edit-btn" onClick={() => openEditCategory(row)} disabled={actionLoading}>
            Edit
          </button>
          <button
            className={row.enabled ? 'mc-disable-btn' : 'mc-enable-btn'}
            onClick={() => handleToggleCategory(row)}
            disabled={actionLoading}
          >
            {row.enabled ? 'Disable' : 'Enable'}
          </button>
        </div>
      )
    }
  ];

  const serviceColumns = [
    { key: 'name', title: 'Service' },
    { key: 'categoryName', title: 'Category' },
    { key: 'gender', title: 'Gender' },
    {
      key: 'defaultDuration',
      title: 'Duration',
      render: (value) => `${value || DEFAULT_DURATION} mins`
    },
    {
      key: 'subServices',
      title: 'Sub Services',
      render: (value) =>
        value && value.length ? (
          <span className="mc-subservices-list">{value.map((s) => s.name).join(', ')}</span>
        ) : (
          <span className="mc-muted">—</span>
        )
    },
    {
      key: 'enabled',
      title: 'Status',
      render: (value) => (
        <span className={`status-badge ${value ? 'status-approved' : 'status-rejected'}`}>
          {value ? 'Enabled' : 'Disabled'}
        </span>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="mc-row-actions">
          <button className="mc-edit-btn" onClick={() => openEditService(row)} disabled={actionLoading}>
            Edit
          </button>
          <button
            className={row.enabled ? 'mc-disable-btn' : 'mc-enable-btn'}
            onClick={() => handleToggleService(row)}
            disabled={actionLoading}
          >
            {row.enabled ? 'Disable' : 'Enable'}
          </button>
        </div>
      )
    }
  ];

  const isEmpty = !loading && categories.length === 0 && services.length === 0;

  return (
    <div className="master-catalog-page">
      <div className="page-header">
        <div className="header-info">
          <h2>Master Service Catalog</h2>
          <p>Central catalog of categories and services used for fast salon service entry</p>
        </div>
        <div className="mc-header-actions">
          <button onClick={handleSeed} className="mc-seed-btn" disabled={loading || actionLoading}>
            Seed Defaults
          </button>
          <button onClick={loadData} className="refresh-btn" disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mc-error-banner">
          <p>{error}</p>
          <button onClick={loadData} className="retry-btn">Try Again</button>
        </div>
      )}

      {isEmpty && (
        <div className="mc-seed-banner">
          <h3>Master catalog is empty</h3>
          <p>Seed it with the default categories and a starter set of common salon services to get going quickly.</p>
          <button onClick={handleSeed} className="mc-seed-btn large" disabled={actionLoading}>
            {actionLoading ? 'Seeding...' : '🌱 Seed Default Catalog'}
          </button>
        </div>
      )}

      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
        >
          Services ({services.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          Categories ({categories.length})
        </button>
      </div>

      {activeTab === 'categories' && (
        <div className="content-section">
          <div className="mc-section-header">
            <h3 className="section-title">Master Categories</h3>
            <button className="mc-add-btn" onClick={openAddCategory} disabled={loading}>
              + Add Category
            </button>
          </div>
          <Table
            columns={categoryColumns}
            data={categories}
            loading={loading}
            emptyMessage="No categories yet — add one or seed the defaults"
          />
        </div>
      )}

      {activeTab === 'services' && (
        <div className="content-section">
          <div className="mc-section-header">
            <h3 className="section-title">Master Services</h3>
            <button className="mc-add-btn" onClick={openAddService} disabled={loading}>
              + Add Service
            </button>
          </div>

          <div className="mc-filters">
            <input
              type="text"
              className="mc-search-input"
              placeholder="Search services..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <select
              className="mc-filter-select"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select
              className="mc-filter-select"
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value)}
            >
              <option value="all">All Genders</option>
              {GENDERS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>

          <Table
            columns={serviceColumns}
            data={filteredServices}
            loading={loading}
            emptyMessage="No services found — add one or seed the defaults"
          />
        </div>
      )}

      {/* Category Add/Edit Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => !actionLoading && setShowCategoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCategory ? 'Edit Category' : 'Add Category'}</h3>
              <button className="modal-close" onClick={() => setShowCategoryModal(false)} disabled={actionLoading}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <label htmlFor="category-name">Category Name *</label>
              <input
                id="category-name"
                type="text"
                className="mc-modal-input"
                placeholder="e.g., Hair, Facial, Nails"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                disabled={actionLoading}
                autoFocus
              />
              <label htmlFor="category-sort">Sort Order</label>
              <input
                id="category-sort"
                type="number"
                className="mc-modal-input"
                placeholder="Lower numbers appear first"
                value={categoryForm.sortOrder}
                onChange={(e) => setCategoryForm({ ...categoryForm, sortOrder: e.target.value })}
                disabled={actionLoading}
              />
            </div>
            <div className="modal-footer">
              <button className="modal-cancel-btn" onClick={() => setShowCategoryModal(false)} disabled={actionLoading}>
                Cancel
              </button>
              <button className="mc-modal-save-btn" onClick={handleCategorySubmit} disabled={actionLoading || !categoryForm.name.trim()}>
                {actionLoading ? 'Saving...' : editingCategory ? 'Save Changes' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service Add/Edit Modal */}
      {showServiceModal && (
        <div className="modal-overlay" onClick={() => !actionLoading && setShowServiceModal(false)}>
          <div className="modal-content mc-service-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingService ? 'Edit Service' : 'Add Service'}</h3>
              <button className="modal-close" onClick={() => setShowServiceModal(false)} disabled={actionLoading}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <label htmlFor="service-name">Service Name *</label>
              <input
                id="service-name"
                type="text"
                className="mc-modal-input"
                placeholder="e.g., Women's Haircut"
                value={serviceForm.name}
                onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                disabled={actionLoading}
                autoFocus
              />

              <div className="mc-form-row">
                <div className="mc-form-field">
                  <label htmlFor="service-category">Category *</label>
                  <select
                    id="service-category"
                    className="mc-modal-input"
                    value={serviceForm.categoryId}
                    onChange={(e) => setServiceForm({ ...serviceForm, categoryId: e.target.value })}
                    disabled={actionLoading}
                  >
                    <option value="">Select category</option>
                    {categories.filter((c) => c.enabled || c.id === serviceForm.categoryId).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="mc-form-field">
                  <label htmlFor="service-gender">Gender</label>
                  <select
                    id="service-gender"
                    className="mc-modal-input"
                    value={serviceForm.gender}
                    onChange={(e) => setServiceForm({ ...serviceForm, gender: e.target.value })}
                    disabled={actionLoading}
                  >
                    {GENDERS.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
                <div className="mc-form-field">
                  <label htmlFor="service-duration">Default Duration</label>
                  <select
                    id="service-duration"
                    className="mc-modal-input"
                    value={serviceForm.defaultDuration}
                    onChange={(e) => setServiceForm({ ...serviceForm, defaultDuration: Number(e.target.value) })}
                    disabled={actionLoading}
                  >
                    {DURATIONS.map((d) => (
                      <option key={d} value={d}>{d} mins</option>
                    ))}
                  </select>
                </div>
              </div>

              <label>Sub Services (optional)</label>
              <p className="mc-field-hint">
                Variants of this service, e.g. Short / Medium / Long. Prices are entered per salon.
              </p>
              {serviceForm.subServices.map((sub, index) => (
                <div key={index} className="mc-subservice-row">
                  <input
                    type="text"
                    className="mc-modal-input"
                    placeholder="Sub service name"
                    value={sub.name}
                    onChange={(e) => updateSubService(index, 'name', e.target.value)}
                    disabled={actionLoading}
                  />
                  <select
                    className="mc-modal-input mc-subservice-duration"
                    value={sub.defaultDuration}
                    onChange={(e) => updateSubService(index, 'defaultDuration', Number(e.target.value))}
                    disabled={actionLoading}
                  >
                    {DURATIONS.map((d) => (
                      <option key={d} value={d}>{d} mins</option>
                    ))}
                  </select>
                  <button
                    className="mc-remove-sub-btn"
                    onClick={() =>
                      setServiceForm((form) => ({
                        ...form,
                        subServices: form.subServices.filter((_, i) => i !== index)
                      }))
                    }
                    disabled={actionLoading}
                    title="Remove sub service"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                className="mc-add-sub-btn"
                onClick={() =>
                  setServiceForm((form) => ({
                    ...form,
                    subServices: [...form.subServices, { name: '', defaultDuration: form.defaultDuration }]
                  }))
                }
                disabled={actionLoading}
              >
                + Add Sub Service
              </button>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel-btn" onClick={() => setShowServiceModal(false)} disabled={actionLoading}>
                Cancel
              </button>
              <button
                className="mc-modal-save-btn"
                onClick={handleServiceSubmit}
                disabled={actionLoading || !serviceForm.name.trim() || !serviceForm.categoryId}
              >
                {actionLoading ? 'Saving...' : editingService ? 'Save Changes' : 'Add Service'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MasterCatalog;
